import { Sparkles, Download, FileText, BookOpen, BarChart3, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

const Resources = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    selectedResources: [] as string[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.company) {
      toast({
        title: "필수 정보 누락",
        description: "이름, 이메일, 전화번호, 회사명은 필수 입력 항목입니다.",
        variant: "destructive",
      });
      return;
    }

    if (formData.selectedResources.length === 0) {
      toast({
        title: "자료 선택 필요",
        description: "최소 1개 이상의 자료를 선택해주세요.",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "이메일 형식 오류",
        description: "올바른 이메일 주소를 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "자료 요청 완료",
      description: `선택하신 자료가 ${formData.email}로 전송됩니다.`,
    });
    
    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      selectedResources: [],
    });
  };

  const toggleResource = (resourceTitle: string) => {
    setFormData(prev => ({
      ...prev,
      selectedResources: prev.selectedResources.includes(resourceTitle)
        ? prev.selectedResources.filter(r => r !== resourceTitle)
        : [...prev.selectedResources, resourceTitle]
    }));
  };

  const resources = [
    {
      icon: FileText,
      title: "KAILA 프레임워크 소개서",
      description: "AI 리터러시 진단 프레임워크의 전체 개요와 핵심 방법론을 담은 소개 자료",
      pages: "24페이지",
      format: "PDF",
      color: "blue",
    },
    {
      icon: BarChart3,
      title: "진단 사례 연구",
      description: "실제 기업 진단 사례와 개선 결과를 담은 케이스 스터디",
      pages: "16페이지",
      format: "PDF",
      color: "purple",
    },
    {
      icon: Shield,
      title: "AI 규제 가이드",
      description: "AI 기본법, EU AI Act 등 주요 규제 동향과 대응 방안",
      pages: "32페이지",
      format: "PDF",
      color: "green",
    },
    {
      icon: BookOpen,
      title: "AI 리터러시 체크리스트",
      description: "조직의 AI 리터러시 수준을 자가 진단할 수 있는 체크리스트",
      pages: "8페이지",
      format: "PDF",
      color: "orange",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/20">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="hidden md:block">
                <span className="text-xl font-bold">KAILA</span>
                <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => navigate("/")}>홈</Button>
              <Button variant="ghost" onClick={() => navigate("/about")}>소개</Button>
              <Button variant="ghost" onClick={() => navigate("/methodology")}>방법론</Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/consultation')}
              >
                상담신청
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 dark:from-blue-500/5 dark:via-transparent dark:to-purple-500/5" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%)]" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-blue-500 text-white">
              <Download className="h-4 w-4 mr-2 inline" />
              자료 다운로드
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              자료 요청
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              KAILA 프레임워크에 대한 상세 자료를 무료로 받아보세요
            </p>
          </div>
        </div>
      </section>

      {/* Request Form Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="text-2xl">자료 요청 신청서</CardTitle>
                <CardDescription>
                  아래 정보를 입력하고 필요한 자료를 선택해주세요
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">이름 *</Label>
                      <Input
                        id="name"
                        placeholder="홍길동"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">이메일 *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="example@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">전화번호 *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="010-1234-5678"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">회사명/기관명 *</Label>
                      <Input
                        id="company"
                        placeholder="(주)회사명"
                        value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  {/* 자료 선택 */}
                  <div className="space-y-4">
                    <Label>요청 자료 선택 *</Label>
                    <p className="text-sm text-muted-foreground">필요한 자료를 모두 선택해주세요</p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {resources.map((resource, index) => {
                        const Icon = resource.icon;
                        const isSelected = formData.selectedResources.includes(resource.title);
                        
                        return (
                          <Card 
                            key={index}
                            className={`cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-2 border-blue-500 bg-blue-500/5' 
                                : 'border-2 border-border hover:border-blue-500/50'
                            }`}
                            onClick={() => toggleResource(resource.title)}
                          >
                            <CardContent className="pt-6">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleResource(resource.title)}
                                    className="w-5 h-5 mt-1 cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Icon className="h-5 w-5 text-blue-500" />
                                    <h4 className="font-bold">{resource.title}</h4>
                                  </div>
                                  <p className="text-sm text-muted-foreground mb-2">
                                    {resource.description}
                                  </p>
                                  <div className="flex gap-2">
                                    <Badge variant="outline" className="text-xs">{resource.pages}</Badge>
                                    <Badge variant="outline" className="text-xs">{resource.format}</Badge>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      자료 요청하기
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => navigate('/')}
                    >
                      취소
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-border bg-gradient-to-r from-blue-500/5 to-purple-500/5">
              <CardContent className="pt-6 text-center">
                <h3 className="font-bold text-xl mb-2">더 자세한 정보가 필요하신가요?</h3>
                <p className="text-muted-foreground mb-4">
                  전문가와 직접 상담하시면 귀사에 맞는 맞춤형 자료를 제공해드립니다.
                </p>
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                  onClick={() => navigate('/consultation')}
                >
                  무료 상담 신청하기
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xl font-bold">KAILA</span>
                    <p className="text-xs text-muted-foreground">AI Literacy Framework</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  기업의 AI 리터러시 수준을 과학적으로 진단하고<br />
                  체계적으로 개선하는 전문 프레임워크
                </p>
              </div>

              <div>
                <h3 className="font-bold mb-4">프레임워크</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    홈
                  </button>
                  <button onClick={() => navigate('/about')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    소개
                  </button>
                  <button onClick={() => navigate('/methodology')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    방법론
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-4">문의</h3>
                <div className="space-y-2 text-sm">
                  <button onClick={() => navigate('/consultation')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    상담 신청
                  </button>
                  <button onClick={() => navigate('/resources')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    자료 요청
                  </button>
                  <button onClick={() => navigate('/partnership')} className="block text-muted-foreground hover:text-foreground transition-colors">
                    파트너십
                  </button>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-sm text-muted-foreground">
                  © 2025 KAILA Framework. All rights reserved.
                </p>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <button onClick={() => navigate('/admin/login')} className="hover:text-foreground transition-colors">
                    관리자 로그인
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
